// Every API call the dialer, leads, tasks and meetings screens make, in one place (paths + response normalisation).
import type {
  Business, CallPrepareRequest, CallPrepareResponse, Campaign, DispositionRequest, DispositionResponse, DropResponse, GateResult,
  Meeting, MeetingStatus, QueueItem, Slot, StateRule, Task, VmRecording,
} from '../../../src/shared/api';
import type { CalendlyLinkRequest, CalendlyUnlinkedResponse, MeetingManualRequestCore } from '../../../src/shared/api-core';
import { api, ApiFailure } from '../api';
import type { DialTarget } from '../../../src/shared/api-numbers';
import type { CallFromNumber, ManualDialRequest, ManualDialResponse, ManualLead, RecentNumber } from '../../../src/shared/api-manual';
import type { CallPace } from '../../../src/shared/api-dialer';
import type { MissedCallList, MissedDoneResponse, MissedScope } from '../../../src/shared/api-missed';
import type { BookedCallDetail, BookedCallList, MeetingWithBookedCall } from '../../../src/shared/api-booked-calls';
import type { ConsentRecordingResponse, ConsentRecordingStopResponse } from '../../../src/shared/api-telephony';
import type {
  SalesEmailContext, SalesEmailDraft, SalesEmailDraftRequest, SalesEmailSendRequest, SalesEmailSendResponse,
} from '../../../src/shared/api-sales-email';
import type { BookingQuestionsResponse as BookingQuestionsView } from '../../../src/shared/api-core';
import type {
  BusinessDetail, BusinessListItem, BusinessPatch, CertificationRow, DncEntry, InboundVoicemail, MeetingBookRequestCore,
  MeetingBookResponse, Page, PendingCall, QueueNextResponseCore, SettingsView, TodayReport, VoiceToken,
} from './types';

function asArray<T>(data: unknown, key: string): T[] {
  if (Array.isArray(data)) return data as T[];
  if (data && typeof data === 'object' && Array.isArray((data as Record<string, unknown>)[key])) return (data as Record<string, T[]>)[key];
  return [];
}

/** A 409 gate_blocked ApiError carries the GateResult in `details`. */
export function gateFromError(e: unknown): GateResult | null {
  if (!(e instanceof ApiFailure) || e.body.error !== 'gate_blocked') return null;
  const d = e.body.details as GateResult | undefined;
  if (d && Array.isArray(d.reasons)) return d;
  return { ok: false, reasons: [{ code: 'campaign_not_live', message: e.body.message }] };
}

export const endpoints = {
  // ---- phone
  voiceToken: () => api.get<VoiceToken>('/api/voice/token'),

  // ---- campaigns / stats
  campaigns: async () => asArray<Campaign>(await api.get<unknown>('/api/campaigns'), 'campaigns'),
  campaign: (id: number) => api.get<Campaign>(`/api/campaigns/${id}`),
  today: () => api.get<TodayReport>('/api/reports/today'),
  // REVIEW-2026-10-01 §4.4: my live pace (dials last hour / today, connects, talk time, last dial).
  pace: () => api.get<CallPace>('/api/calls/pace'),
  // GET /api/settings is readable by setters (CORE routes/settings.ts); mailingAddress null = not set.
  settings: () => api.get<SettingsView>('/api/settings'),
  certifications: async (userId: number) => asArray<CertificationRow>(await api.get<unknown>(`/api/training/certifications?userId=${userId}`), 'certifications'),

  // ---- queue
  queueNext: (campaignId: number, skipMembershipIds: number[]) =>
    api.post<QueueNextResponseCore>('/api/queue/next', { campaignId, skipMembershipIds }),
  queueRelease: (membershipId: number) => api.post<unknown>('/api/queue/release', { membershipId }),
  queueItem: (membershipId: number) => api.get<QueueItem>(`/api/queue/item/${membershipId}`),

  // ---- calls
  // hookId / openerVariant (optional): the drawer's hook, for dial_signal_snapshots (LEADGEN-SPEC §13 CORE 3).
  // callFromNumberId (dial pad "Call from", SPEC §19 2026-10-02): this call only; omitted = Automatic.
  // redialConfirmed (PJ 2026-10-20): the setter said Yes to "Call this number again?"; omitted = no yes.
  // target (PJ 2026-10-23): 'store' for "Call the store" (A), 'owner' / 'store' for the other line; omitted = the default.
  // meetingConfirmed (PJ 2026-10-28): the setter said Yes to "Meeting booked. Call anyway?".
  prepare: (membershipId: number, extra?: Pick<CallPrepareRequest, 'hookId' | 'openerVariant' | 'callFromNumberId' | 'redialConfirmed' | 'meetingConfirmed' | 'target'>) =>
    api.post<CallPrepareResponse>('/api/calls/prepare', { membershipId, ...extra } satisfies CallPrepareRequest),
  /** A no-campaign pad call (no lead row): the same gated prepare, keyed by the business. */
  prepareNoCampaign: (businessId: number, callFromNumberId?: number | null, redialConfirmed?: boolean, target?: DialTarget | null) =>
    api.post<CallPrepareResponse>('/api/calls/prepare', {
      noCampaign: true, businessId, ...(callFromNumberId ? { callFromNumberId } : {}), ...(redialConfirmed ? { redialConfirmed: true } : {}),
      ...(target ? { target } : {}),
    }),
  pending: async (): Promise<PendingCall | null> => {
    const data = await api.get<unknown>('/api/calls/pending');
    const call = data && typeof data === 'object' && 'call' in (data as object) ? (data as { call: unknown }).call : data;
    if (!call || typeof call !== 'object' || typeof (call as PendingCall).id !== 'number') return null;
    return call as PendingCall;
  },
  // Dial pad (SPEC §19): find-or-create the number as my own lead; recent numbers; my setter-entered leads.
  manualDial: (req: ManualDialRequest) => api.post<ManualDialResponse>('/api/calls/manual', req),
  recentNumbers: async () => asArray<RecentNumber>(await api.get<unknown>('/api/calls/recent'), 'recent'),
  manualLeads: async (q: string) => asArray<ManualLead>(await api.get<unknown>(`/api/calls/manual/leads?q=${encodeURIComponent(q)}&limit=50`), 'leads'),
  noCampaignItem: (businessId: number) => api.get<QueueItem>(`/api/calls/manual/item/${businessId}`),
  callFromNumbers: async () => asArray<CallFromNumber>(await api.get<unknown>('/api/calls/numbers'), 'numbers'),
  hangup: (callId: number) => api.post<unknown>(`/api/calls/${callId}/hangup`),
  dispose: (callId: number, req: DispositionRequest) => api.post<DispositionResponse>(`/api/calls/${callId}/disposition`, req),
  drop: (callId: number, recordingId: number) => api.post<DropResponse>(`/api/calls/${callId}/drop`, { recordingId }),
  // PJ 2026-10-20: record the rest of a connected all-party-consent call after the prospect's yes; stop when they take it back.
  recordConsent: (callId: number) => api.post<ConsentRecordingResponse>(`/api/calls/${callId}/record-consent`),
  stopConsentRecording: (callId: number) => api.post<ConsentRecordingStopResponse>(`/api/calls/${callId}/record-consent/stop`),
  recordings: async () => asArray<VmRecording>(await api.get<unknown>('/api/recordings'), 'recordings'),
  // Post-call email follow-up (SPEC §19, 2026-10-02): panel context, AI draft, review-first send.
  emailContext: (callId: number) => api.get<SalesEmailContext>(`/api/calls/${callId}/email`),
  emailDraft: (callId: number, req: SalesEmailDraftRequest) => api.post<SalesEmailDraft>(`/api/calls/${callId}/email/draft`, req),
  emailSend: (callId: number, req: SalesEmailSendRequest) => api.post<SalesEmailSendResponse>(`/api/calls/${callId}/email/send`, req),

  // ---- meetings
  // "Book into" (2026-10-11): eventSlug is the link chosen in the panel; omitted = the lead's default (the server decides).
  slots: async (membershipId: number, eventSlug?: string | null) =>
    asArray<Slot>(await api.get<unknown>(`/api/meetings/slots?membershipId=${membershipId}${slugParam(eventSlug)}`), 'slots'),
  /** The chosen link's Calendly choice questions, so the Book panel offers the right answers, plus the links this lead
   *  may book (the server's view of the cannabis lock). */
  bookingQuestions: (membershipId: number, eventSlug?: string | null) =>
    api.get<BookingQuestionsView>(`/api/meetings/questions?membershipId=${membershipId}${slugParam(eventSlug)}`),
  book: (req: MeetingBookRequestCore) => api.post<MeetingBookResponse>('/api/meetings/book', req),
  /** "Booked elsewhere": a campaign lead by membershipId, a no-campaign pad call by businessId (2026-10-20). */
  bookManual: (req: MeetingManualRequestCore) => api.post<MeetingBookResponse>('/api/meetings/manual', req),
  // PJ 2026-10-20: bookings made on a Calendly link the dialer hasn't linked (the ids only steer "Likely theirs"), and
  // linking one to the lead on screen (logged Meeting booked like a Book-panel booking) or, for an admin, any business.
  calendlyUnlinked: (q: { callId?: number | null; membershipId?: number | null; businessId?: number | null } = {}) => {
    const p = new URLSearchParams();
    if (q.callId) p.set('callId', String(q.callId));
    if (q.membershipId) p.set('membershipId', String(q.membershipId));
    if (q.businessId) p.set('businessId', String(q.businessId));
    return api.get<CalendlyUnlinkedResponse>(`/api/meetings/calendly-unlinked?${p}`);
  },
  linkCalendly: (req: CalendlyLinkRequest) => api.post<MeetingBookResponse>('/api/meetings/link-calendly', req),
  meetings: async (q: { from?: string; to?: string; status?: MeetingStatus | ''; mine?: boolean }) => {
    const p = new URLSearchParams();
    if (q.from) p.set('from', q.from);
    if (q.to) p.set('to', q.to);
    if (q.status) p.set('status', q.status);
    if (q.mine) p.set('mine', '1');
    return asArray<MeetingWithBookedCall>(await api.get<unknown>(`/api/meetings?${p}`), 'meetings');
  },
  // Booked calls (migration 0027): every booked call for team review, one meeting's call with its transcript.
  // campaignId 'none' (PJ 2026-10-20): the booked calls of no-campaign pad calls.
  bookedCalls: (q: { setterId?: number | ''; campaignId?: number | 'none' | ''; status?: MeetingStatus | ''; view?: 'all' | 'upcoming' }) => {
    const p = new URLSearchParams();
    if (q.view && q.view !== 'all') p.set('view', q.view);
    if (q.setterId) p.set('setterId', String(q.setterId));
    if (q.campaignId) p.set('campaignId', String(q.campaignId));
    if (q.status) p.set('status', q.status);
    return api.get<BookedCallList>(`/api/meetings/booked-calls?${p}`);
  },
  bookedCall: (meetingId: number) => api.get<BookedCallDetail>(`/api/meetings/${meetingId}/booked-call`),
  /** Admin: write a brief that gave up again, now. */
  retryBookedCallBrief: (meetingId: number) => api.post<BookedCallDetail>(`/api/meetings/${meetingId}/booked-call/retry`),
  updateMeeting: (id: number, body: { status: MeetingStatus; outcomeNote?: string }) => api.patch<Meeting>(`/api/meetings/${id}`, body),

  // ---- tasks / inbound voicemail
  tasks: async (q: { scope: 'mine' | 'all'; status: 'open' | 'done' }) =>
    asArray<Task>(await api.get<unknown>(`/api/tasks?scope=${q.scope}&status=${q.status}`), 'tasks'),
  updateTask: (id: number, body: { done?: boolean; dueAt?: string; note?: string }) => api.patch<Task>(`/api/tasks/${id}`, body),
  voicemails: async (status: 'open' | 'all' = 'all') => asArray<InboundVoicemail>(await api.get<unknown>(`/api/inbound/voicemails?status=${status}`), 'voicemails'),
  voicemailAudioUrl: (id: number) => `/api/inbound/voicemails/${id}/audio`,
  voicemailHandled: (id: number) => api.post<unknown>(`/api/inbound/voicemails/${id}/handled`),
  // ---- missed calls (PJ 2026-10-24): the page, and Mark done with an optional note
  missedCalls: (scope: MissedScope) => api.get<MissedCallList>(`/api/inbound/missed-calls?scope=${scope}`),
  missedDone: (callId: number, note: string | null) => api.post<MissedDoneResponse>(`/api/inbound/missed-calls/${callId}/done`, note ? { note } : {}),

  // ---- leads (CORE contract: src/shared/api-core.ts)
  searchLeads: (q: { q?: string; campaignId?: number | null; status?: string; state?: string; limit?: number; offset?: number }) => {
    const p = new URLSearchParams();
    if (q.q) p.set('q', q.q);
    if (q.campaignId) p.set('campaignId', String(q.campaignId));
    if (q.status) p.set('status', q.status);
    if (q.state) p.set('state', q.state); // 'US-OK' (CORE: GET /api/businesses?state=)
    p.set('limit', String(q.limit ?? 50));
    p.set('offset', String(q.offset ?? 0));
    return api.get<Page<BusinessListItem>>(`/api/businesses?${p}`);
  },
  business: (id: number) => api.get<BusinessDetail>(`/api/businesses/${id}`),
  /** Readable by setters (GET /api/settings/state-rules has no admin check): the Leads state filter and its labels. */
  stateRules: async () => asArray<StateRule>(await api.get<unknown>('/api/settings/state-rules'), 'rules'),
  updateBusiness: (id: number, patch: BusinessPatch) => api.patch<Business>(`/api/businesses/${id}`, patch),
  addBusinessToDnc: (id: number, reason: string) => api.post<DncEntry>(`/api/businesses/${id}/dnc`, { reason }),
};

export type { BookingQuestionsResponse as BookingQuestionsView } from '../../../src/shared/api-core';

function slugParam(eventSlug: string | null | undefined): string {
  return eventSlug ? `&eventSlug=${encodeURIComponent(eventSlug)}` : '';
}
